import React, { type ReactNode } from "react";
import { Building2, Calendar, Clock, Wallet } from "@semo/icons";
import { cn } from "../../utils";
import {
  mapApiStatusToUiStatus,
  UI_STATUS_CONFIG,
  formatDateForDisplay,
} from "./utils";
import type { QuoteRequestListItemDto } from "@semo/api-contracts";
import { BaseDashboardCard } from "../dashboard-card/BaseDashboardCard";
import { getPaymentChannelLabel } from "./paymentChannel";

export interface ProjectCardProps {
  project: QuoteRequestListItemDto;
  onClick?: () => void;
  /** Custom action element to replace the default "상세보기" button */
  action?: ReactNode;
}

export const ProjectCard = React.memo(function ProjectCard({
  project,
  onClick,
  action,
}: ProjectCardProps) {
  const uiStatus = mapApiStatusToUiStatus(project.status);
  const statusInfo = UI_STATUS_CONFIG[uiStatus];
  const StatusIcon = statusInfo.icon;

  // 발주기관명은 공개 공고에서만 내려온다(비공개 공고·기관명 없는 구매자는 null).
  // null 이면 행 자체를 빼서 기존 레이아웃을 그대로 유지한다.
  const organizationName = project.organizationName?.trim();

  const infoItems = [
    ...(organizationName
      ? [
          {
            label: "발주기관",
            value: (
              <span
                className="block max-w-[180px] truncate"
                title={organizationName}
              >
                {organizationName}
              </span>
            ),
            icon: Building2,
          },
        ]
      : []),
    // 신설(2026-08-19) 이전 공고는 값이 null 이라 행 자체를 뺀다 — 고른 적 없는 공고에
    // 「미결정」이 붙으면 고른 것처럼 읽힌다.
    ...(getPaymentChannelLabel(project.preferredPaymentChannel)
      ? [
          {
            label: "결제방법",
            value: getPaymentChannelLabel(project.preferredPaymentChannel)!,
            icon: Wallet,
          },
        ]
      : []),
    {
      label: "마감일",
      value: formatDateForDisplay(project.deadline),
      icon: Calendar,
    },
    {
      label: "등록일",
      value: formatDateForDisplay(String(project.createdAt ?? "")),
      icon: Clock,
    },
  ];

  const footer = action ?? (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      className="w-full py-3 bg-white text-gray-600 border border-gray-200 rounded-[12px] text-sm font-semibold hover:bg-primary/5 hover:text-primary hover:border-primary/20 transition-all"
    >
      상세보기
    </button>
  );

  return (
    <BaseDashboardCard
      idLabel={project.requestNumber}
      statusBadge={
        <span
          className={cn(
            "h-[28px] px-2.5 rounded-[8px] text-2xs font-medium flex items-center justify-center gap-1.5 border",
            statusInfo.bgColor,
            statusInfo.textColor,
            uiStatus === "in-progress" && "border-primary/20",
            uiStatus === "closed" && "border-green-200",
            uiStatus === "on-hold" && "border-orange-200",
          )}
        >
          <StatusIcon className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="leading-none">{statusInfo.label}</span>
        </span>
      }
      title={project.projectName}
      infoItems={infoItems}
      footer={footer}
      onClick={onClick}
    />
  );
});

ProjectCard.displayName = "ProjectCard";
